<template>
  <div class="btn" :class="[size, type]">
    <q-btn unelevated v-bind="$attrs" no-caps v-on="$listeners">
      <!-- @slot Default slot for button content -->
      <slot />

      <svg
        v-if="type === 'link'"
        class="q-ml-xs"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.0075 8.25H3.75C3.3375 8.25 3 8.5875 3 9C3 9.4125 3.3375 9.75 3.75 9.75H12.0075V11.0925C12.0075 11.43 12.4125 11.595 12.645 11.355L14.73 9.2625C14.8725 9.1125 14.8725 8.88 14.73 8.73L12.645 6.6375C12.4125 6.3975 12.0075 6.57 12.0075 6.9V8.25Z"
          fill="currentColor"
        />
      </svg>
    </q-btn>
  </div>
</template>

<script>
/**
 * This is a component for using custom buttons
 */
export default {
  inheritAttrs: false,
  props: {
    /**
     *  the size of  the button
     */
    size: {
      type: String,
      default: 'big',
      validator: value => ['small', 'big'].includes(value)
    },

    /**
     *  the type of the button
     */
    type: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'secondary', 'danger', 'ghost', 'text', 'tertiary', 'link'].includes(value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/fonts';
@import '~@/styles/shadows';
@import '~@/styles/general';

@mixin primary {
  ::v-deep .q-btn {
    background: var(--r-primary-1);
    color: var(--r-white-1);

    &:hover {
      @extend .shadow2;
    }

    &:focus {
      box-shadow: inset 0 4px 4px rgba(0, 130, 142, 0.25);
    }

    &[disabled] {
      background: var(--r-white-3);
      color: var(--r-white-5);
    }
  }
}
@mixin secondary {
  ::v-deep .q-btn {
    background: transparent;
    color: var(--r-primary-1);
    border: 1px solid var(--r-primary-1);

    &:hover {
      background: var(--r-white-1);
      @extend .shadow2;
      color: var(--r-primary-4);
      border: 1px solid var(--r-primary-4);
    }

    &:focus {
      background: var(--r-white-1);
      color: var(--r-primary-2);
      border: 1px solid var(--r-primary-2);
    }

    &[disabled] {
      background: transparent;
      color: var(--r-white-5);
      border: 1px solid var(--r-white-3);
    }
  }
}
@mixin danger {
  ::v-deep .q-btn {
    background: var(--r-danger-1);
    color: var(--r-white-1);

    &:hover {
      @extend .shadow2;
    }

    &:focus {
      box-shadow: inset 0 4px 4px var(--r-danger-2);
    }

    &[disabled] {
      background: var(--r-white-3);
      color: var(--r-white-5);
    }
  }
}
@mixin ghost {
  ::v-deep .q-btn {
    background: transparent;
    color: var(--r-secondary-1);
    border: 1px solid var(--r-white-5);

    &:hover {
      background: var(--r-white-1);
      @extend .shadow2;
      border: 1px solid var(--r-secondary-4);
      color: var(--r-secondary-2);
    }

    &:focus {
      border: 1px solid var(--r-secondary-1);
      color: var(--r-secondary-2);
    }

    &[disabled] {
      background: transparent;
      color: var(--r-white-5);
      border: 1px solid var(--r-white-3);
    }
  }
}
@mixin text {
  ::v-deep .q-btn {
    background: transparent;
    color: var(--r-primary-1);

    &:hover {
      color: var(--r-primary-4);
    }

    &:focus {
      color: var(--r-primary-2);
    }

    &[disabled] {
      color: var(--r-white-5);
    }
  }
}
@mixin tertiary {
  ::v-deep .q-btn {
    background: var(--r-secondary-1);
    color: var(--r-white-1);

    &:hover {
      @extend .shadow2;
    }

    &:focus {
      box-shadow: inset 0 4px 4px rgba(51, 54, 76, 0.25);
    }

    &[disabled] {
      background: var(--r-white-3);
      color: var(--r-white-5);
    }
  }
}

@mixin link {
  ::v-deep .q-btn {
    background: transparent;
    color: var(--r-link-blue);
    @extend .text-body-1;
    text-decoration: underline;

    &__content {
      padding: var(--r-space-sm);
    }

    &:active {
      color: var(--r-link-focus);
    }
  }
}

@mixin big {
  ::v-deep .q-btn {
    &__content {
      padding: var(--r-space-md) var(--r-space-lg);
      @content;
    }
  }
}

@mixin small {
  ::v-deep .q-btn {
    &__content {
      padding: var(--r-space-sm);
      @content;
    }
  }
}

.btn {
  &.primary {
    @include primary;

    &.big {
      @include big {
        @extend .text-button-big;
      }
    }

    &.small {
      @include small {
        @extend .text-caption-2;
      }
    }
  }

  &.secondary {
    @include secondary;

    &.big {
      @include big {
        @extend .text-button-big;
      }
    }

    &.small {
      @include small {
        @extend .text-caption-2;
      }
    }
  }

  &.danger {
    @include danger;

    &.big {
      @include big {
        @extend .text-button-big;
      }
    }

    &.small {
      @include small {
        @extend .text-caption-2;
      }
    }
  }

  &.ghost {
    @include ghost;

    &.big {
      @include big {
        @extend .text-body-4;
      }
    }

    &.small {
      @include small {
        @extend .text-lowercase-btn;
      }
    }
  }

  &.text {
    @include text;

    &.big {
      @include big {
        @extend .text-button-big;
      }
    }

    &.small {
      @include small {
        @extend .text-overline-1;
      }
    }
  }

  &.tertiary {
    @include tertiary;

    &.big {
      @include big {
        @extend .text-button-big;
      }
    }

    &.small {
      @include small {
        @extend .text-caption-2;
      }
    }
  }

  &.link {
    @include link;
  }

  ::v-deep .q-btn {
    @extend .border-radius-sm;

    .q-focus-helper {
      display: none;
    }

    &__wrapper {
      padding: 0;
      min-height: unset;
    }

    &__content {
      transition: none;
    }

    &[disabled] {
      pointer-events: none;
      opacity: 1 !important;
    }
  }
}
</style>

<docs>
  ### Primary button

  ```jsx
    <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
      <RBtn>Primary big</RBtn>
      <RBtn disabled>Primary big disabled</RBtn>

      <RBtn size="small">Primary small</RBtn>
      <RBtn size="small" disabled>Primary small disabled</RBtn>
    </Grid>
  ```

  ### Secondary button

  ```jsx
  <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
    <RBtn type="secondary">Secondary big</RBtn>
    <RBtn type="secondary" disabled>Secondary big disabled</RBtn>

    <RBtn size="small" type="secondary">Secondary small</RBtn>
    <RBtn size="small" type="secondary" disabled>Secondary small disabled</RBtn>
  </Grid>
  ```

  ### Danger button

  ```jsx
  <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
    <RBtn type="danger">Danger big</RBtn>
    <RBtn type="danger" disabled>Danger big disabled</RBtn>

    <RBtn size="small" type="danger">Danger small</RBtn>
    <RBtn size="small" type="danger" disabled>Danger small disabled</RBtn>
  </Grid>
  ```

  ### Ghost button

  ```jsx
  <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
    <RBtn type="ghost">Ghost big</RBtn>
    <RBtn type="ghost" disabled>Ghost big disabled</RBtn>

    <RBtn size="small" type="ghost">Ghost small</RBtn>
    <RBtn size="small" type="ghost" disabled>Ghost small disabled</RBtn>
  </Grid>
  ```

  ### Text button

  ```jsx
  <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
    <RBtn type="text">Text big</RBtn>
    <RBtn type="text" disabled>Text big disabled</RBtn>

    <RBtn size="small" type="text">Text small</RBtn>
    <RBtn size="small" type="text" disabled>Text small disabled</RBtn>
  </Grid>
  ```

  ### Tertiary button

  ```jsx
  <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
    <RBtn type="tertiary">Tertiary big</RBtn>
    <RBtn type="tertiary" disabled>Tertiary big disabled</RBtn>

    <RBtn size="small" type="tertiary">Tertiary small</RBtn>
    <RBtn size="small" type="tertiary" disabled>Tertiary small disabled</RBtn>
  </Grid>
  ```

  ### Link button

  ```jsx
  <Grid class="q-pa-md" gaf="column" justify-content="start" align-items="center" gap="10px">
    <RBtn size="small" type="link">link to another page</RBtn>
  </Grid>
  ```
</docs>
